import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import LeftMenu from '../../components/leftMenu';
import Pagination from '../../components/pagination';
import translations from '../../data/translation';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import { Config } from '../../common/config';

const SEARCH_FIELDS = [
  { value: 'userName', labelKey: 'searchFieldNameDefault' },
  { value: 'mobile', labelKey: 'phoneNumber' },
  { value: 'email', labelKey: 'email' },
];

function getAgeGroup(birthday, t) {
  if (!birthday) return '-';
  const birth = new Date(birthday);
  if (isNaN(birth.getTime())) return '-';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  if (!t) return '-';
  if (age < 20) return t.ageGroup10s;   
  if (age < 30) return t.ageGroup20s;
  if (age < 40) return t.ageGroup30s;
  if (age < 50) return t.ageGroup40s;
  return t.ageGroup50plus;
}

function formatGender(gender, t) {
  if (!t) return '-';
  if (gender === 'M') return t.male;
  if (gender === 'F') return t.female;
  return '-';
}

function formatDate(dateString) {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toISOString().substring(0, 10);
  } catch {
    return '-';
  }
}

/** 전화번호를 010-XXXX-XXXX 형식으로 통일 (+82 → 010 변환 포함) */
function formatPhoneTo010(phone) {
  if (!phone || typeof phone !== 'string') return '-';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 0) return '-';
  let normalized = digits;
  if (digits.startsWith('82')) {
    if (digits.length === 11 || digits.length === 12) {
      normalized = '0' + digits.slice(2);
    } else if (digits.length === 10) {
      normalized = '0' + digits;
    }
  }
  if (normalized.startsWith('01') && normalized.length === 11) {
    return `${normalized.slice(0, 3)}-${normalized.slice(3, 7)}-${normalized.slice(7)}`;
  }
  return phone;
}

export default function CustomerManage() {
  const navigate = useNavigate();
  const storeNo = useStoreNo();
  const { fetchData } = useAxiosFetch();
  const language = useSelector(state => state.language.language);
  const t = translations[language];

  const pageSize = Config.PAGE_SIZE_LIST[0];
  const [rawList, setRawList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchField, setSearchField] = useState('userName');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [appliedSearch, setAppliedSearch] = useState({
    field: 'userName',
    keyword: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrd, setSortOrd] = useState('desc'); // 등록일 정렬: desc=최신순, asc=과거순

  const totalPage = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedList = rawList;

  useEffect(() => {
    const load = async () => {
      try {
        const persisted = localStorage.getItem('persist:root');
        if (!persisted) {
          navigate('/login', { replace: true });
          return;
        }
        setIsLoading(true);
        const params = new URLSearchParams();
        params.set('pageNo', String(currentPage));
        params.set('pageSize', String(pageSize));
        params.set('sortOrd', sortOrd);
        if (appliedSearch.keyword.trim()) {
          const paramKey = appliedSearch.field;
          params.set(paramKey, appliedSearch.keyword.trim());
        }
        const ret = await fetchData({
          url: `${Config.API_SERVER}store-user-list?${params.toString()}`,
          method: 'get',
        });
        const data = ret?.data?.data;
        setRawList(Array.isArray(data?.list) ? data.list : []);
        setTotalCount(data?.total ?? 0);
      } catch (e) {
        setRawList([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [
    storeNo,
    fetchData,
    navigate,
    currentPage,
    pageSize,
    appliedSearch,
    sortOrd,
  ]);

  const handleSearch = () => {
    setAppliedSearch({ field: searchField, keyword: searchKeyword });
    setCurrentPage(1);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const toggleSortOrd = () => {
    setSortOrd(prev => (prev === 'desc' ? 'asc' : 'desc'));
    setCurrentPage(1);
  };

  return (
    <>
      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.home}
            </NavLink>
          </li>
          <li>{t.customerManagement || '고객 정보 관리'}</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="customer" />

          <section className="rightcontant_box">
            <h2 className="tit_01">
              {t.customerManagement || '고객 정보 관리'}
            </h2>

            <article className="search_card">
              <div className="search_row">
                <div className="search_field">
                  <span className="field_label">{t.searchConditionLabel}</span>
                  <select
                    value={searchField}
                    onChange={e => setSearchField(e.target.value)}
                  >
                    {SEARCH_FIELDS.map(f => (
                      <option key={f.value} value={f.value}>
                        {t[f.labelKey]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="search_field">
                  <span className="field_label">{t.searchKeyword}</span>
                  <input
                    type="text"
                    placeholder={t.searchKeyword}
                    value={searchKeyword}
                    onChange={e => setSearchKeyword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="search_btns">
                  <button
                    type="button"
                    className="button_05 button_black"
                    onClick={handleSearch}
                  >
                    {t.search}
                  </button>
                </div>
              </div>
            </article>

            <p className="table_01" style={{ marginBottom: '16px' }}>
              {language === 'ko'
                ? `전체 고객 목록 (총 ${totalCount}명)`
                : `Total customers (${totalCount})`}
            </p>

            {isLoading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                {t.loading}
              </div>
            ) : (
              <>
                <div className="table_wrapper">
                  <table className="table_box_01">
                    <colgroup>
                      <col width="120px" />
                      <col width="220px" />
                      <col width="140px" />
                      <col width="80px" />
                      <col width="100px" />
                      <col width="120px" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>{t.name}</th>
                        <th>{t.email}</th>
                        <th>{t.phoneNumber}</th>
                        <th>{t.gender}</th>
                        <th>{t.ageGroup}</th>
                        <th>
                          <button
                            type="button"
                            className="sort_btn_inline"
                            onClick={toggleSortOrd}
                            title={
                              sortOrd === 'desc'
                                ? language === 'ko'
                                  ? '최신순 (클릭 시 과거순)'
                                  : 'Newest first'
                                : language === 'ko'
                                  ? '과거순 (클릭 시 최신순)'
                                  : 'Oldest first'
                            }
                          >
                            {t.registrationDate}
                            <span
                              style={{ fontSize: '13px', marginLeft: '2px' }}
                            >
                              {sortOrd === 'desc' ? ' ↓' : ' ↑'}
                            </span>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedList.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            style={{
                              textAlign: 'center',
                              padding: '48px',
                              color: '#94a3b8',
                              fontSize: '16px',
                            }}
                          >
                            {language === 'ko'
                              ? '표시할 고객 정보가 없습니다.'
                              : 'No customer data to display.'}
                          </td>
                        </tr>
                      ) : (
                        paginatedList.map((item, index) => (
                          <tr key={item.storeUserNo || index}>
                            <td>
                              {item.user?.userName ? (
                                item.user.userName
                              ) : (
                                <span className="cell-empty">—</span>
                              )}
                            </td>
                            <td>
                              {item.user?.email ? (
                                item.user.email
                              ) : (
                                <span className="cell-empty">—</span>
                              )}
                            </td>
                            <td>
                              {item.user?.mobile ? (
                                formatPhoneTo010(item.user.mobile)
                              ) : (
                                <span className="cell-empty">—</span>
                              )}
                            </td>
                            <td>
                              {item.user?.gender ? (
                                formatGender(item.user.gender, t)
                              ) : (
                                <span className="cell-empty">—</span>
                              )}
                            </td>
                            <td>
                              {item.user?.birthday ? (
                                getAgeGroup(item.user.birthday, t)
                              ) : (
                                <span className="cell-empty">—</span>
                              )}
                            </td>
                            <td>
                              {item.regDttm ? (
                                formatDate(item.regDttm)
                              ) : (
                                <span className="cell-empty">—</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <ul className="page_box">
                  <Pagination
                    totalPage={totalPage}
                    onPageChange={p => setCurrentPage(p)}
                  />
                </ul>
              </>
            )}
          </section>
        </section>
      </main>
    </>
  );
}
